import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit, FiSave, FiX, FiDollarSign, FiUsers, FiTrendingUp,
  FiCheckCircle, FiSettings, FiCalendar, FiClock, FiDownload, FiTrash2,
  FiToggleLeft, FiToggleRight, FiCpu, FiDatabase, FiArchive
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@components/common';

// Types
interface PricingStructure {
  baseFee: {
    monthly: number;
    annual: number;
    billingCycle: 'monthly' | 'annual';
  };
  perScorePrice: number;
  scoresUsed: number;
  scoresIncluded: number;
}

interface AdditionalService {
  id: string;
  name: string;
  description: string;
  price: number;
  enabled: boolean;
}

interface SubscriptionHistory {
  id: string;
  timestamp: string;
  action: string;
  user: string;
  details: string;
}

interface Subscription {
  id: string;
  clientId: string;
  clientName: string;
  status: 'active' | 'suspended' | 'expired';
  pricing: PricingStructure;
  additionalServices: AdditionalService[];
  aiConfigAccess: boolean;
  startDate: string;
  endDate: string;
  lastModified: string;
  history: SubscriptionHistory[];
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const BackButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: white;
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0;
`;

const Subtitle = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const StatusBanner = styled.div<{ status: string }>`
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => {
    switch (props.status) {
      case 'active': return `${theme.colors.success}20`;
      case 'suspended': return `${theme.colors.warning}20`;
      case 'expired': return `${theme.colors.error}20`;
      default: return theme.colors.surface;
    }
  }};
  border-left: 4px solid ${props => {
    switch (props.status) {
      case 'active': return theme.colors.success;
      case 'suspended': return theme.colors.warning;
      case 'expired': return theme.colors.error;
      default: return theme.colors.surface;
    }
  }};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const PricingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const PricingBox = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${theme.colors.primary}30;
`;

const PricingLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
  margin-bottom: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const PricingValue = styled.div`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
`;

const PricingUnit = styled.span`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
  font-weight: ${theme.typography.weights.normal};
`;

const BillingCycleToggle = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.xs};
  background: white;
  border-radius: ${theme.borderRadius.md};
`;

const ToggleButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${props => props.active ? theme.colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : theme.colors.text};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => props.active ? theme.colors.primary : theme.colors.surface};
  }
`;

const ScoreUsageBox = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}30;
  border-radius: ${theme.borderRadius.md};
`;

const UsageBar = styled.div`
  width: 100%;
  height: 12px;
  background: ${theme.colors.surface};
  border-radius: 6px;
  overflow: hidden;
  margin: ${theme.spacing.md} 0;
`;

const UsageFill = styled.div<{ percentage: number }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props =>
    props.percentage > 90 ? theme.colors.error :
    props.percentage > 70 ? theme.colors.warning :
    theme.colors.success
  };
  transition: width ${theme.transitions.base};
`;

const UsageDetails = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
`;

const ServicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ServiceItem = styled.div<{ $enabled: boolean }>`
  padding: ${theme.spacing.md};
  background: ${props => props.$enabled ? `${theme.colors.success}10` : `${theme.colors.surface}50`};
  border: 2px solid ${props => props.$enabled ? theme.colors.success : theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all ${theme.transitions.fast};
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const ServiceDescription = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
`;

const ServicePrice = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin: 0 ${theme.spacing.md};
`;

const ToggleSwitch = styled.button<{ $enabled: boolean }>`
  width: 48px;
  height: 24px;
  background: ${props => props.$enabled ? theme.colors.success : theme.colors.surface};
  border: none;
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    top: 2px;
    left: ${props => props.$enabled ? '26px' : '2px'};
    transition: all ${theme.transitions.fast};
    box-shadow: ${theme.shadows.sm};
  }

  &:hover {
    opacity: 0.8;
  }
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  max-height: 400px;
  overflow-y: auto;
`;

const HistoryItem = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface}30;
  border-left: 3px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xs};
`;

const HistoryAction = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const HistoryTime = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const HistoryDetails = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
  margin-bottom: ${theme.spacing.xs};
`;

const HistoryUser = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const TotalBox = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  color: white;
  text-align: center;
  margin-top: ${theme.spacing.lg};
`;

const TotalLabel = styled.div`
  font-size: ${theme.typography.sizes.base};
  margin-bottom: ${theme.spacing.sm};
  opacity: 0.9;
`;

const TotalValue = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
`;

const AIConfigButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.lg};
  background: ${theme.colors.info};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};
  box-shadow: ${theme.shadows.md};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const SubscriptionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  // Mock data
  const subscription: Subscription = {
    id: id || 'SUB-001',
    clientId: 'CLI-001',
    clientName: 'Office HLM Paris',
    status: 'active',
    pricing: {
      baseFee: {
        monthly: 1290,
        annual: 13900, // ~10% discount
        billingCycle: 'monthly'
      },
      perScorePrice: 2.50,
      scoresUsed: 1247,
      scoresIncluded: 1000
    },
    additionalServices: [
      {
        id: '1',
        name: 'Customisation',
        description: 'Interface personnalisée aux couleurs du client',
        price: 150,
        enabled: true
      },
      {
        id: '2',
        name: 'Maintenance Premium',
        description: 'Support prioritaire 24/7 avec SLA garantis',
        price: 300,
        enabled: true
      },
      {
        id: '3',
        name: 'Stockage Cloud Étendu',
        description: 'Stockage illimité pour documents et médias',
        price: 200,
        enabled: false
      }
    ],
    aiConfigAccess: true,
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    lastModified: '2024-01-20T15:30:00',
    history: [
      {
        id: '1',
        timestamp: '2024-01-20T15:30:00',
        action: 'Service activé',
        user: 'admin@pradiumtech.com',
        details: 'Activation du service "Maintenance Premium" (+300 FCFA/mois)'
      },
      {
        id: '2',
        timestamp: '2024-01-15T10:20:00',
        action: 'Modification tarifs',
        user: 'admin@pradiumtech.com',
        details: 'Prix unitaire par score modifié: 2.00 FCFA → 2.50 FCFA'
      },
      {
        id: '3',
        timestamp: '2024-01-10T14:45:00',
        action: 'Configuration IA',
        user: 'tech@pradiumtech.com',
        details: 'Ajustement des paramètres de scoring pour le client'
      },
      {
        id: '4',
        timestamp: '2024-01-01T09:00:00',
        action: 'Abonnement créé',
        user: 'admin@pradiumtech.com',
        details: 'Création abonnement Enterprise - 1290 FCFA/mois'
      }
    ]
  };

  const [services, setServices] = useState(subscription.additionalServices);

  const toggleService = (serviceId: string) => {
    setServices(services.map(s =>
      s.id === serviceId ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const calculateTotal = () => {
    const baseFee = billingCycle === 'monthly'
      ? subscription.pricing.baseFee.monthly
      : subscription.pricing.baseFee.annual;

    const servicesFee = services
      .filter(s => s.enabled)
      .reduce((sum, s) => sum + s.price, 0);

    const extraScores = Math.max(0, subscription.pricing.scoresUsed - subscription.pricing.scoresIncluded);
    const scoresFee = extraScores * subscription.pricing.perScorePrice;

    return {
      baseFee,
      servicesFee: billingCycle === 'monthly' ? servicesFee : servicesFee * 12,
      scoresFee,
      total: baseFee + (billingCycle === 'monthly' ? servicesFee : servicesFee * 12) + scoresFee
    };
  };

  const total = calculateTotal();
  const usagePercentage = (subscription.pricing.scoresUsed / subscription.pricing.scoresIncluded) * 100;

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate('/superadmin/subscriptions')}>
              <FiArrowLeft /> Retour
            </BackButton>
            <div>
              <Title>Abonnement - {subscription.clientName}</Title>
              <Subtitle>ID: {subscription.id}</Subtitle>
            </div>
          </HeaderLeft>
          <ActionButtons>
            {isEditing ? (
              <>
                <Button variant="primary" onClick={() => setIsEditing(false)}>
                  <FiSave /> Enregistrer
                </Button>
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  <FiX /> Annuler
                </Button>
              </>
            ) : (
              <>
                <Button variant="primary" onClick={() => setIsEditing(true)}>
                  <FiEdit /> Modifier
                </Button>
                <Button variant="outline">
                  <FiDownload /> Exporter
                </Button>
                <Button variant="outline" style={{ color: theme.colors.error, borderColor: theme.colors.error }}>
                  <FiTrash2 /> Supprimer
                </Button>
              </>
            )}
          </ActionButtons>
        </Header>

        <StatusBanner status={subscription.status}>
          <div>
            <div style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '70' }}>
              Statut de l'abonnement
            </div>
            <div style={{ fontSize: theme.typography.sizes.xl, fontWeight: theme.typography.weights.bold, marginTop: theme.spacing.xs }}>
              {subscription.status === 'active' && <><FiCheckCircle style={{ color: theme.colors.success }} /> Actif</>}
              {subscription.status === 'suspended' && 'Suspendu'}
              {subscription.status === 'expired' && 'Expiré'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '70' }}>
              Période
            </div>
            <div style={{ fontSize: theme.typography.sizes.base, fontWeight: theme.typography.weights.semibold, marginTop: theme.spacing.xs }}>
              {new Date(subscription.startDate).toLocaleDateString('fr-FR')} - {new Date(subscription.endDate).toLocaleDateString('fr-FR')}
            </div>
          </div>
        </StatusBanner>

        <ContentGrid>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
            <Card>
              <CardTitle>
                <FiDollarSign /> Structure Tarifaire
              </CardTitle>
              <PricingSection>
                <PricingBox>
                  <PricingLabel>
                    <FiCalendar /> Droit d'utilisation
                  </PricingLabel>
                  <PricingValue>
                    {billingCycle === 'monthly'
                      ? subscription.pricing.baseFee.monthly
                      : subscription.pricing.baseFee.annual} FCFA
                    <PricingUnit>/ {billingCycle === 'monthly' ? 'mois' : 'an'}</PricingUnit>
                  </PricingValue>
                  <BillingCycleToggle>
                    <ToggleButton
                      active={billingCycle === 'monthly'}
                      onClick={() => setBillingCycle('monthly')}
                    >
                      Mensuel
                    </ToggleButton>
                    <ToggleButton
                      active={billingCycle === 'annual'}
                      onClick={() => setBillingCycle('annual')}
                    >
                      Annuel (-10%)
                    </ToggleButton>
                  </BillingCycleToggle>
                </PricingBox>

                <ScoreUsageBox>
                  <PricingLabel>
                    <FiTrendingUp /> Prix unitaire par score de candidat
                  </PricingLabel>
                  <PricingValue>
                    {subscription.pricing.perScorePrice} FCFA
                    <PricingUnit>/ score</PricingUnit>
                  </PricingValue>
                  <UsageBar>
                    <UsageFill percentage={usagePercentage} />
                  </UsageBar>
                  <UsageDetails>
                    <span>{subscription.pricing.scoresUsed} scores utilisés</span>
                    <span>{subscription.pricing.scoresIncluded} inclus</span>
                  </UsageDetails>
                  {subscription.pricing.scoresUsed > subscription.pricing.scoresIncluded && (
                    <div style={{
                      marginTop: theme.spacing.md,
                      padding: theme.spacing.sm,
                      background: theme.colors.warning + '20',
                      borderRadius: theme.borderRadius.sm,
                      fontSize: theme.typography.sizes.sm,
                      color: theme.colors.warning
                    }}>
                      +{subscription.pricing.scoresUsed - subscription.pricing.scoresIncluded} scores supplémentaires = {
                        ((subscription.pricing.scoresUsed - subscription.pricing.scoresIncluded) * subscription.pricing.perScorePrice).toFixed(2)
                      } FCFA
                    </div>
                  )}
                </ScoreUsageBox>
              </PricingSection>
            </Card>

            <Card>
              <CardTitle>
                <FiSettings /> Services Additionnels
              </CardTitle>
              <ServicesList>
                {services.map(service => (
                  <ServiceItem key={service.id} $enabled={service.enabled}>
                    <ServiceInfo>
                      <ServiceName>{service.name}</ServiceName>
                      <ServiceDescription>{service.description}</ServiceDescription>
                    </ServiceInfo>
                    <ServicePrice>+{service.price} FCFA/mois</ServicePrice>
                    <ToggleSwitch
                      $enabled={service.enabled}
                      onClick={() => toggleService(service.id)}
                      disabled={!isEditing}
                    />
                  </ServiceItem>
                ))}
              </ServicesList>

              <TotalBox>
                <TotalLabel>Total {billingCycle === 'monthly' ? 'Mensuel' : 'Annuel'}</TotalLabel>
                <TotalValue>{total.total.toLocaleString()} FCFA</TotalValue>
                <div style={{ fontSize: theme.typography.sizes.sm, marginTop: theme.spacing.md, opacity: 0.9 }}>
                  Base: {total.baseFee} FCFA | Services: {total.servicesFee} FCFA | Scores: {total.scoresFee.toFixed(2)} FCFA
                </div>
              </TotalBox>
            </Card>

            <Card>
              <CardTitle>
                <FiArchive /> Historique des Modifications
              </CardTitle>
              <HistoryList>
                {subscription.history.map(item => (
                  <HistoryItem key={item.id}>
                    <HistoryHeader>
                      <HistoryAction>{item.action}</HistoryAction>
                      <HistoryTime>
                        <FiClock /> {new Date(item.timestamp).toLocaleString('fr-FR')}
                      </HistoryTime>
                    </HistoryHeader>
                    <HistoryDetails>{item.details}</HistoryDetails>
                    <HistoryUser>Par: {item.user}</HistoryUser>
                  </HistoryItem>
                ))}
              </HistoryList>
            </Card>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
            <Card>
              <CardTitle>
                <FiCpu /> Configuration IA
              </CardTitle>
              <div style={{ marginBottom: theme.spacing.lg }}>
                <div style={{
                  padding: theme.spacing.md,
                  background: subscription.aiConfigAccess ? `${theme.colors.success}20` : `${theme.colors.error}20`,
                  borderRadius: theme.borderRadius.md,
                  marginBottom: theme.spacing.md
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                    color: subscription.aiConfigAccess ? theme.colors.success : theme.colors.error,
                    fontWeight: theme.typography.weights.semibold
                  }}>
                    {subscription.aiConfigAccess ? <FiCheckCircle /> : <FiX />}
                    {subscription.aiConfigAccess ? 'Accès activé' : 'Accès désactivé'}
                  </div>
                </div>

                <div style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '70', marginBottom: theme.spacing.lg }}>
                  Accédez à l'interface de configuration individuelle pour ajuster les paramètres IA de ce client:
                  <ul style={{ marginTop: theme.spacing.md, paddingLeft: theme.spacing.lg }}>
                    <li>Pondération des critères de scoring</li>
                    <li>Seuils de validation personnalisés</li>
                    <li>Règles métier spécifiques</li>
                    <li>Configuration de l'algorithme IA</li>
                    <li>Modèles et niveau de personnalisation</li>
                  </ul>
                </div>

                <AIConfigButton
                  onClick={() => navigate(`/superadmin/ai-config/${subscription.clientId}`)}
                  disabled={!subscription.aiConfigAccess}
                >
                  <FiSettings /> Accéder à la Configuration IA
                </AIConfigButton>
              </div>
            </Card>

            <Card>
              <CardTitle>Actions Rapides</CardTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <Button variant="outline" onClick={() => navigate(`/superadmin/clients/${subscription.clientId}`)}>
                  <FiUsers /> Voir le Client
                </Button>
                <Button variant="outline" onClick={() => setIsEditing(!isEditing)}>
                  <FiEdit /> {isEditing ? 'Annuler Modifications' : 'Modifier Abonnement'}
                </Button>
                <Button variant="outline">
                  <FiDownload /> Télécharger Facture
                </Button>
                <Button variant="outline">
                  <FiDatabase /> Exporter Paramètres
                </Button>
                <Button variant="outline" style={{ color: theme.colors.warning, borderColor: theme.colors.warning }}>
                  <FiToggleLeft /> Suspendre Abonnement
                </Button>
              </div>
            </Card>
          </div>
        </ContentGrid>
      </Container>
    </DashboardLayout>
  );
};
